import { useRef, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

function loadImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode failed"));
    img.src = url;
  });
}

async function decodeToSource(file) {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        cleanup: () => bitmap.close && bitmap.close(),
      };
    } catch (e) {
      // fall through to the object-URL path (e.g. formats createImageBitmap can't decode)
    }
  }
  const url = URL.createObjectURL(file);
  const img = await loadImageFromUrl(url);
  return {
    source: img,
    width: img.width,
    height: img.height,
    cleanup: () => {
      img.src = "";
      URL.revokeObjectURL(url);
    },
  };
}

// Memory-safe compression: never hold the full-resolution photo as a base64
// string in memory (the cause of "not enough memory" on phones). We decode
// straight from the file (createImageBitmap when available, else an object URL)
// and draw a downscaled copy to a small canvas before exporting a JPEG.
async function compress(file, maxDim = 1280, quality = 0.6) {
  const { source, width, height, cleanup } = await decodeToSource(file);
  try {
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(source, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    // release canvas memory promptly
    canvas.width = 0;
    canvas.height = 0;
    return dataUrl;
  } finally {
    cleanup();
  }
}

export default function PhotoUpload({ photos = [], onChange, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    const added = [];
    let failed = 0;
    for (const f of files) {
      try {
        const dataUrl = await compress(f);
        added.push({ id: crypto.randomUUID(), name: f.name, dataUrl });
      } catch (err) {
        failed += 1;
      }
      // yield to the event loop so the browser can free memory between photos
      await new Promise((r) => setTimeout(r, 0));
    }
    if (added.length) onChange([...photos, ...added]);
    if (failed) toast.error(`Couldn't add ${failed} photo${failed > 1 ? "s" : ""}. Try again or take fewer at once.`);
    setBusy(false);
  };

  const remove = (id) => onChange(photos.filter((p) => p.id !== id));

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`${testid}-add`}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 text-sm font-semibold text-primary active:scale-[0.99] transition-transform disabled:opacity-60"
      >
        {busy ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Processing…
          </>
        ) : (
          <>
            <Camera className="h-5 w-5" /> Add photos
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={add}
        data-testid={`${testid}-input`}
      />
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p) => (
            <div key={p.id} className="relative aspect-square">
              <img
                src={p.dataUrl}
                alt={p.name}
                className="h-full w-full rounded-lg object-cover border border-slate-200"
              />
              <button
                type="button"
                onClick={() => remove(p.id)}
                data-testid={`${testid}-remove-${p.id}`}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-red-500 p-1 text-white shadow"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
